/**
 * Akun & keamanan (spec 2026-09-30-akun-dan-keamanan): the words both sides share. Which
 * roles must use the second step, the two refusal codes the API answers and the app turns
 * into screens, the length of a password a person chooses and the hint that names what
 * makes it weak, the masked e-mail the recovery screen shows, and the recovery code's
 * limits. Pure and dependency-free: the API, the session module and the screens import it.
 */
import { PERAN, type Peran } from './schema/contract'
import { utf8Length } from './sync/ops'

/**
 * Spec 2, as the owner extended it on 2026-10-02: every role must use the second step (it was
 * the two admin roles). An account without a factor is asked to set one up at its next online
 * use; a new account replaces its initial password first, then sets up the app.
 */
export const PERAN_WAJIB_DUA_LANGKAH: readonly Peran[] = PERAN

export function wajibDuaLangkah(peran: Peran): boolean {
  return PERAN_WAJIB_DUA_LANGKAH.includes(peran)
}

/** The API's two refusals (spec 6); the app opens a screen for each instead of an error. */
export const KODE_LANGKAH = ['harus_ganti_sandi', 'perlu_verifikasi_dua_langkah'] as const
export type KodeLangkah = (typeof KODE_LANGKAH)[number]
/** The screen each refusal opens: a new password, or the code (or the enrolment). */
export type LangkahAkun = 'ganti_sandi' | 'dua_langkah'

export function isKodeLangkah(value: unknown): value is KodeLangkah {
  return typeof value === 'string' && (KODE_LANGKAH as readonly string[]).includes(value)
}

export function langkahDariKode(kode: KodeLangkah): LangkahAkun {
  return kode === 'harus_ganti_sandi' ? 'ganti_sandi' : 'dua_langkah'
}

/** The assurance level of a Supabase session: aal2 after the second step. */
export type AalSesi = 'aal1' | 'aal2'

/**
 * The account steps a route exists to satisfy (spec 2026-09-30 akun, 6): the password route
 * passes the initial-password rule (and the second step while the account has no verified
 * factor), the two recovery steps pass both. Every other route: null.
 */
export type Lolos = 'kata_sandi' | 'pemulihan' | null

/**
 * The `amr` methods of a session an e-mail link opened (GoTrue): the reset, an e-mail OTP or
 * magic link, an e-mail change, an invitation, a sign-up confirmation.
 */
export const METODE_TAUTAN_EMAIL: ReadonlySet<string> = new Set([
  'recovery',
  'otp',
  'magiclink',
  'email_change',
  'invite',
  'email/signup',
])

/**
 * A session an e-mail link opened and the password never did (final review, 2026-10-01). The
 * link proves the address only, and a copy of it (a mail scanner that opened an old link, a
 * forwarded mail) is as good as the original: its session may set the password and nothing
 * else (spec 5.4). An app's code added since does not make it a sign-in: an account without
 * an app can enrol one from the link's own session. Read from the methods SATU knows, so a
 * method GoTrue adds beside the link's (`token_refresh`, say) never widens it; a token without
 * `amr` (the tests') is none.
 */
export function sesiTautanEmail(amr: readonly { readonly method: string }[]): boolean {
  return (
    amr.some((a) => METODE_TAUTAN_EMAIL.has(a.method)) && !amr.some((a) => a.method === 'password')
  )
}

/**
 * The rule of spec 6, pure; the API's gate applies it to every request. A user whose role
 * requires the second step (every role since the owner's ruling of 2026-10-02,
 * `wajibDuaLangkah`), or who has a verified factor, needs an aal2 token; a user with
 * `harus_ganti_sandi` may only replace it. With a factor the code comes first; without one the
 * password comes first (5.1 before 5.2), then the enrolment. A role that requires the second
 * step needs a verified factor behind its aal2 token too (ruling, 2026-10-01): one raised with
 * an app since reset or recovered is taken through enrolment. A session an e-mail link opened
 * (`sesiTautan`) is refused like an initial password, but only the password route passes it:
 * the recovery steps need a password sign-in.
 */
export function periksaLangkah(o: {
  peran: Peran
  aal: AalSesi
  adaFaktor: boolean
  harusGantiSandi: boolean
  sesiTautan?: boolean
  lolos: Lolos
}): KodeLangkah | null {
  const gantiLolos = o.lolos === 'kata_sandi' || o.lolos === 'pemulihan'
  const gantiDulu =
    (o.harusGantiSandi && !gantiLolos) || (o.sesiTautan === true && o.lolos !== 'kata_sandi')
  if (o.aal === 'aal2') {
    if (gantiDulu) return 'harus_ganti_sandi'
    if (wajibDuaLangkah(o.peran) && !o.adaFaktor && o.lolos === null) {
      return 'perlu_verifikasi_dua_langkah'
    }
    return null
  }
  if (o.adaFaktor && o.lolos !== 'pemulihan') return 'perlu_verifikasi_dua_langkah'
  if (gantiDulu) return 'harus_ganti_sandi'
  if (wajibDuaLangkah(o.peran) && !o.adaFaktor && o.lolos === null) {
    return 'perlu_verifikasi_dua_langkah'
  }
  return null
}

/**
 * The step the API would ask of a session at an ordinary route (the enrolment, /sesi) whatever
 * the account's role, read from what the app holds before it asks: the token's own claims and,
 * when known, whether the account has a verified factor (GoTrue's sign-in answer lists them).
 * Null when the token names no assurance level, when the session passes, or when the step could
 * differ by role or by a factor not known: the app then asks the API, which decides.
 *
 * Since every role requires the second step (2026-10-02) an aal1 session is always refused for
 * a step, so a sign-in that asked to enrol with it met a 403 every time (uji A30, B20, C27): the
 * app now goes to the step's screen at once and enrols with the session the step leaves.
 */
export function langkahSesiTanpaPeran(o: {
  aal: AalSesi | undefined
  adaFaktor: boolean | null
  harusGantiSandi: boolean
  sesiTautan: boolean
}): KodeLangkah | null {
  const { aal } = o
  if (aal === undefined) return null
  const faktor = o.adaFaktor === null ? [true, false] : [o.adaFaktor]
  const kode = faktor.flatMap((adaFaktor) =>
    PERAN.map((peran) =>
      periksaLangkah({
        peran,
        aal,
        adaFaktor,
        harusGantiSandi: o.harusGantiSandi,
        sesiTautan: o.sesiTautan,
        lolos: null,
      }),
    ),
  )
  const [pertama = null] = kode
  return pertama !== null && kode.every((k) => k === pertama) ? pertama : null
}

/** Spec 4: "length at least 10". */
export const KATA_SANDI_MIN = 10
/**
 * An initial password an admin sets: at least 8 characters (code points). The person replaces
 * it at the first sign-in (`harus_ganti_sandi`); the 72-byte maximum is the same.
 */
export const KATA_SANDI_AWAL_MIN = 8
/**
 * GoTrue refuses longer passwords: bcrypt reads 72 BYTES, so the limit is in UTF-8 bytes,
 * not characters ("é" is two). Check it with `lebihDariMaks`, never `.length` or `.max()`.
 */
export const KATA_SANDI_MAKS = 72

/** True when the password is longer than bcrypt (and so GoTrue) takes: over 72 UTF-8 bytes. */
export function lebihDariMaks(sandi: string): boolean {
  return utf8Length(sandi) > KATA_SANDI_MAKS
}

/**
 * What is wrong with an initial password an admin sets (creation, "Atur ulang"), or null: fewer
 * than 8 characters (code points, as `[...sandi]` counts them), or more than 72 bytes. The API
 * refuses the same two (`pastikanKataSandiAwal`); the screens say it before any request.
 */
export function masalahSandiAwal(sandi: string): 'terlalu_pendek' | 'terlalu_panjang' | null {
  if ([...sandi].length < KATA_SANDI_AWAL_MIN) return 'terlalu_pendek'
  if (lebihDariMaks(sandi)) return 'terlalu_panjang'
  return null
}

/** What an admin paroki may create, change or reset inside its parish (spec 2026-09-30, 6). */
export const PERAN_DIKELOLA_PAROKI: readonly Peran[] = [
  'staf_paroki',
  'ketua_lingkungan',
  'ketua_stasi',
]

/**
 * May `aktor` manage (create, change, deactivate, reset) an account with these attributes? The
 * admin keuskupan any account; an admin paroki the staff and ketua accounts of its own parish,
 * so never another admin and never itself. The API's rule (`mayManageUser`) is this one, and
 * the screens offer exactly what it allows (spec 2026-09-30 akun 5.5).
 */
export function bolehKelolaAkun(
  aktor: { peran: Peran; parokiId: string | null },
  sasaran: { peran: Peran; parokiId: string | null },
): boolean {
  if (aktor.peran === 'admin_keuskupan') return true
  if (aktor.peran !== 'admin_paroki' || aktor.parokiId === null) return false
  return PERAN_DIKELOLA_PAROKI.includes(sasaran.peran) && sasaran.parokiId === aktor.parokiId
}

export type KelemahanSandi =
  | 'terlalu_pendek'
  | 'sama_dengan_email'
  | 'memuat_nama'
  | 'hanya_angka'
  | 'berulang'
  | 'urutan'
  | 'umum'

export interface NilaiSandi {
  /** At least KATA_SANDI_MIN characters: the one rule that blocks. */
  cukupPanjang: boolean
  /** What makes it weak, in the order the hint names them; advice beyond the length. */
  kelemahan: KelemahanSandi[]
}

const URUTAN = ['01234567890123456789', 'abcdefghijklmnopqrstuvwxyz', 'qwertyuiopasdfghjklzxcvbnm']
const URUTAN_BALIK = URUTAN.map((u) => [...u].reverse().join(''))
const UMUM = new Set([
  'katasandi',
  'katasandi1',
  'katasandi123',
  'rahasia123',
  'rahasiasaja',
  'bismillah123',
  'tuhanyesus',
  'yesuskristus',
  'haleluya123',
  'simuagats',
  'satuagats',
  'keuskupanagats',
  'admin12345',
  'qwerty12345',
  'password123',
])

/** Spec 4: "the hint names what makes it weak, e.g. terlalu pendek, sama dengan e-mail". */
export function nilaiKataSandi(
  sandi: string,
  konteks: { email?: string | null | undefined; nama?: string | null | undefined } = {},
): NilaiSandi {
  const huruf = [...sandi]
  const kecil = sandi.toLowerCase()
  const kelemahan: KelemahanSandi[] = []
  if (huruf.length < KATA_SANDI_MIN) kelemahan.push('terlalu_pendek')
  const email = konteks.email?.trim().toLowerCase() ?? ''
  if (email !== '' && (kecil === email || kecil === email.split('@')[0])) {
    kelemahan.push('sama_dengan_email')
  }
  const kataNama = (konteks.nama ?? '')
    .toLowerCase()
    .split(/[^\p{L}]+/u)
    .filter((k) => [...k].length >= 3)
  if (kataNama.some((k) => kecil.includes(k))) kelemahan.push('memuat_nama')
  if (huruf.length > 0 && /^\d+$/.test(sandi)) kelemahan.push('hanya_angka')
  if (huruf.length > 1 && new Set(huruf.map((h) => h.toLowerCase())).size <= 2) {
    kelemahan.push('berulang')
  }
  if (
    huruf.length >= 4 &&
    (URUTAN.some((u) => u.includes(kecil)) || URUTAN_BALIK.some((u) => u.includes(kecil)))
  ) {
    kelemahan.push('urutan')
  }
  if (UMUM.has(kecil)) kelemahan.push('umum')
  return { cukupPanjang: huruf.length >= KATA_SANDI_MIN, kelemahan }
}

/**
 * "maria.kayaru@satu-e2e.test" → "m***u@satu-e2e.test": enough to recognise, not to read.
 * The last letter shows only past four letters, so a short local part ("abc") never gives
 * away most of itself.
 */
export function samarkanEmail(email: string): string {
  const at = email.lastIndexOf('@')
  if (at <= 0) return '***'
  const lokal = [...email.slice(0, at)]
  const awal = lokal[0] ?? ''
  const akhir = lokal.length > 4 ? (lokal.at(-1) ?? '') : ''
  return `${awal}***${akhir}@${email.slice(at + 1)}`
}

/**
 * Spec 5.3: six digits, valid 10 minutes, at most 5 attempts, at most 3 codes an hour. The
 * day's limits (final review, 2026-10-01): at most 6 codes and 10 attempts in a rolling 24
 * hours per account, so the hour's limit no longer allows 360 guesses a day, nor 72 mails
 * from one account against the provider's 100 a day.
 */
export const KODE_PEMULIHAN = {
  digit: 6,
  berlakuMenit: 10,
  percobaanMaks: 5,
  perJamMaks: 3,
  perHariMaks: 6,
  percobaanPerHariMaks: 10,
} as const
